import { useTranslation } from 'react-i18next';

import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { VOICE_PRESET_OPTIONS } from '@/constants/voicePresets';
import { useVoicePreset } from '@/hooks/useVoicePreset';
import type { VoicePreset } from '@/shared/types/integration';

import LanguageField from './LanguageField';
import CardBase from '../../Components/CardBase';

/**
 * Configuration card for voice integrations.
 *
 * Exposes the preset selector (language presets bundle recommended STT/TTS/noise settings) and the
 * language field. Editing any advanced field switches the preset to Custom.
 *
 * `showLanguageField` gates the language selector behind verification (telnyx/twilio pass their
 * verified status; web has no verification and leaves it on by default).
 */
const Configuration = ({ showLanguageField = true }: { showLanguageField?: boolean }) => {
  const { t } = useTranslation();
  const { preset, applyPreset, disabled } = useVoicePreset();

  return (
    <CardBase title={t('integrations.voice_common.configuration.title')}>
      <div className="flex flex-col gap-2">
        <Label htmlFor="voice-preset">
          {t('integrations.voice_common.configuration.preset_label')}
        </Label>
        <Select
          value={preset}
          onValueChange={(value: VoicePreset) => applyPreset(value)}
          disabled={disabled}
        >
          <SelectTrigger id="voice-preset" className="w-full">
            <SelectValue placeholder={t('integrations.voice_common.configuration.preset_label')} />
          </SelectTrigger>
          <SelectContent>
            {VOICE_PRESET_OPTIONS.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {t(option.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {t('integrations.voice_common.configuration.preset_help')}
        </p>
      </div>

      {showLanguageField && <LanguageField />}
    </CardBase>
  );
};

export default Configuration;
